<template>
    <div>
        <!-- Page Intro -->
        <div class="page-intro card-style">
            <h1 style="bold">📄 Frequently Asked Questions (FAQ)</h1>
            <p class="intro-text">
                Find answers to common questions about ASDL IoT, its sensor networks, data collection, and how to get
                involved.
            </p>
        </div>

        <!-- FAQ Section -->
        <div class="faq-section">
            <div class="container">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="accordion" id="accordionFaq">
                            <div class="card-item" v-for="(faq, index) in faqs" :key="index">
                                <button class="accordion-button" :class="{ open: openIndex === index }"
                                    @click="toggle(index)">
                                    {{ faq.question }}
                                    <span class="plus-icon">{{ openIndex === index ? '−' : '+' }}</span>
                                </button>
                                <div class="accordion-body" v-show="openIndex === index">
                                    <p>{{ faq.answer }}</p>
                                </div>
                            </div>
                        </div>

                        <a id="terms-anchor"></a>
                        <TermsContent />
                    </div>

                    <div class="col-xl-4 col-lg-4">
                        <div class="form-section card-style">
                            <h6>📝 Have a Question?</h6>
                            <form action="#">
                                <input type="text" name="name" placeholder="Full Name" required />
                                <input type="email" name="email" placeholder="Your Email" required />
                                <textarea name="message" placeholder="How can we help you?" required></textarea>
                                <button type="submit" class="btn-primary">Submit Request</button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import TermsContent from "@/components/TermsContent.js";

export default {
    name: "FAQ",
    components: { TermsContent },
    data() {
        return {
            faqs: [],
            openIndex: null,
        };
    },
    async mounted() {
        window.scrollTo(0, 0);
        try {
            const response = await axios.get("/assets/data/faq.json");
            this.faqs = response.data.faq;
        } catch (error) {
            console.error("Error loading FAQ data:", error);
        }
    },
    methods: {
        toggle(index) {
            this.openIndex = this.openIndex === index ? null : index;
        },
    },
};
</script>

<style scoped>
:root {
    --gt-gold: #B3A369;
    --gt-lt-gold: #BFB37C;
    --gt-md-gold: #A4925A;
    --gt-drk-gold: #857437;
    --gt-blktxt: #333333;
    --gt-mdgray: #545454;
    --gt-drkgray: #3B3B3B;
    --gt-pimile-light: #F2F3F1;
}

h1 {
    font-weight: bold;
}

/* Card Style (Intro + Form) */
.card-style {
    background: var(--gt-pimile-light);
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
    margin-bottom: 40px;
}

/* Page Intro */
.page-intro h1 {
    font-family: "Roboto Condensed", sans-serif;
    color: var(--gt-md-gold);
    font-size: 2rem;
    margin-bottom: 15px;
}

.intro-text {
    font-family: "Roboto", sans-serif;
    font-size: 1.1rem;
    font-weight: 300;
    color: var(--gt-blktxt);
}

/* Accordion Items */
.card-item {
    margin-bottom: 15px;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    background: white;
    transition: all 0.3s;
}

/* Accordion Button Override */
.accordion-button {
    width: 100%;
    padding: 20px 25px;
    font-family: "Roboto", sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: var(--gt-blktxt);
    background: white;
    border: none;
    text-align: left;
    position: relative;
    cursor: pointer;
    transition: all 0.3s;
    border-radius: 12px 12px 0 0;
}

/* Hover closed */
.accordion-button:not(.open):hover {
    background: var(--gt-md-gold);
    color: var(--gt-blktxt);
}

/* Open state */
.accordion-button.open {
    background: var(--gt-pimile-light);
    color: var(--gt-blktxt);
}

/* Hover open */
.accordion-button.open:hover {
    background: var(--gt-md-gold);
    color: var(--gt-blktxt);
}

/* Plus/Minus */
.plus-icon {
    position: absolute;
    right: 20px;
    font-size: 1.5rem;
    transition: transform 0.3s ease;
    color: var(--gt-gold);
}

/* Accordion Content Override */
.accordion-body {
    padding: 20px 25px;
    background: #ffffff;
    border-top: 1px solid #eee;

    /* Apply global body text override here */
    font-family: "Roboto", sans-serif;
    font-weight: 400;
    font-size: 1rem;
    color: var(--gt-blktxt);
    line-height: 1.5rem;
}

/* Form Section */
.form-section h6 {
    font-family: "Roboto Condensed", sans-serif;
    color: var(--gt-drk-gold);
    font-size: 1.3rem;
    margin-bottom: 20px;
}

.form-section input,
.form-section textarea {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 15px;
    font-family: "Roboto", sans-serif;
    font-size: 0.95rem;
}

/* Primary Button */
.btn-primary {
    background: var(--gt-gold);
    color: var(--gt-blktxt);
    padding: 12px 24px;
    font-family: "Roboto Condensed", sans-serif;
    text-transform: uppercase;
    font-size: 0.85rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    transition: background 0.3s;
}

.btn-primary:hover {
    background: var(--gt-lt-gold);
}
</style>
